<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFavoriteWhatsAppTemplates } from 'dashboard/composables/useFavoriteWhatsAppTemplates';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  inboxId: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();
const dialogRef = ref(null);
const templateParserRef = ref(null);
const state = reactive({
  scheduledAt: '',
  templateId: null,
});

const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);
const {
  isFavoriteTemplate,
  toggleFavoriteTemplate,
  favoriteTemplatesFrom,
  sortTemplatesByFavorite,
} = useFavoriteWhatsAppTemplates(computed(() => props.inboxId));

const currentDateTime = computed(() => {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const templateOptions = computed(() => {
  if (!props.inboxId) return [];

  return sortTemplatesByFavorite(
    getFilteredWhatsAppTemplates.value(props.inboxId)
  ).map(template => ({
    value: `${template.name}::${template.language || 'en'}`,
    label: `${template.name} (${template.language || 'en'})`,
    template,
  }));
});

const favoriteTemplates = computed(() =>
  favoriteTemplatesFrom(getFilteredWhatsAppTemplates.value(props.inboxId)).slice(
    0,
    6
  )
);

const selectedTemplate = computed(
  () =>
    templateOptions.value.find(option => option.value === state.templateId)
      ?.template || null
);

const isTemplateValid = computed(() => {
  if (!selectedTemplate.value) return false;
  return templateParserRef.value?.v$?.$invalid === false || true;
});

const isFormValid = computed(() => {
  return (
    Boolean(state.scheduledAt) &&
    Boolean(selectedTemplate.value) &&
    isTemplateValid.value
  );
});

const resetState = () => {
  state.scheduledAt = '';
  state.templateId = null;
};

const close = () => {
  dialogRef.value?.close();
  resetState();
};

const open = () => {
  resetState();
  dialogRef.value?.open();
};

const buildPayload = () => {
  const template = selectedTemplate.value;
  const parser = templateParserRef.value;

  return {
    snoozedUntil: Math.floor(new Date(state.scheduledAt).getTime() / 1000),
    scheduledTemplate: {
      name: template?.name || '',
      namespace: template?.namespace || '',
      category: template?.category || 'UTILITY',
      language: template?.language || 'en_US',
      processed_params: parser?.processedParams || {},
    },
  };
};

const selectFavoriteTemplate = template => {
  state.templateId = `${template.name}::${template.language || 'en'}`;
};

const handleConfirm = () => {
  if (!isFormValid.value) return;

  emit('submit', buildPayload());
  close();
};

watch(
  () => props.inboxId,
  () => {
    state.templateId = null;
  }
);

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    :title="t('CONVERSATION.RESCHEDULE_MODAL.TITLE')"
    :description="t('CONVERSATION.RESCHEDULE_MODAL.DESCRIPTION')"
    :confirm-button-label="t('CONVERSATION.RESCHEDULE_MODAL.CONFIRM')"
    :cancel-button-label="t('CONVERSATION.RESCHEDULE_MODAL.CANCEL')"
    :disable-confirm-button="!isFormValid"
    @confirm="handleConfirm"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="state.scheduledAt"
        type="datetime-local"
        :min="currentDateTime"
        :label="t('CONVERSATION.RESCHEDULE_MODAL.DATE_LABEL')"
      />

      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('CONVERSATION.RESCHEDULE_MODAL.TEMPLATE_LABEL') }}
        </label>
        <div
          v-if="favoriteTemplates.length"
          class="mb-2 flex flex-wrap gap-2 rounded-lg border border-n-weak bg-n-alpha-1 p-2"
        >
          <Button
            v-for="template in favoriteTemplates"
            :key="`favorite-${template.name}-${template.language}`"
            type="button"
            xs
            slate
            faded
            icon="i-lucide-star"
            :label="template.name"
            class="!w-fit"
            @click="selectFavoriteTemplate(template)"
          />
        </div>
        <ComboBox
          v-model="state.templateId"
          :options="templateOptions"
          :placeholder="t('CONVERSATION.RESCHEDULE_MODAL.TEMPLATE_PLACEHOLDER')"
        />
        <div
          v-if="selectedTemplate"
          class="mt-2 flex items-center justify-end"
        >
          <Button
            type="button"
            xs
            slate
            ghost
            icon="i-lucide-star"
            :label="
              isFavoriteTemplate(selectedTemplate)
                ? t('CONVERSATION.RESCHEDULE_MODAL.FAVORITES.REMOVE')
                : t('CONVERSATION.RESCHEDULE_MODAL.FAVORITES.ADD')
            "
            @click="toggleFavoriteTemplate(selectedTemplate)"
          />
        </div>
      </div>

      <WhatsAppTemplateParser
        v-if="selectedTemplate"
        ref="templateParserRef"
        :template="selectedTemplate"
      />
    </div>
  </Dialog>
</template>
